import clsx from 'clsx'
import Card from 'components/Card'
import {
    formatNativeAppDate,
    formatNativeAppMinutes,
    formatNativeAppSeconds,
    useProviderNativeAppStats,
} from 'hooks/providerNativeAppHooks'

interface ProviderNativeAppStatsProps {
    providerId: number | string
}

interface ActivityDotProps {
    active: boolean
    label: string
}

const ActivityDot = ({ active, label }: ActivityDotProps) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-neutral-600">
        <span
            className={clsx(
                'inline-block h-2 w-2 rounded-full',
                active ? 'bg-active-green' : 'bg-neutral-300',
            )}
        />
        {label}
    </span>
)

const ProviderNativeAppStats = ({ providerId }: ProviderNativeAppStatsProps) => {
    const { loading, report } = useProviderNativeAppStats(providerId)

    if (loading && !report) {
        return (
            <Card header="Native App" useToggle>
                <p className="text-md text-neutral-500">Loading...</p>
            </Card>
        )
    }

    if (!report?.has_native_app) {
        return (
            <Card header="Native App" useToggle>
                <p className="text-md text-neutral-500">No native app installed</p>
            </Card>
        )
    }

    return (
        <Card header="Native App" useToggle>
            <div className="space-y-3 text-md">
                {report.devices && report.devices.length > 0 && (
                    <div className="space-y-1">
                        {report.devices.map((d, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-sm font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200/50 uppercase">
                                    {d.platform}
                                </span>
                                <span className="text-neutral-800 font-medium">
                                    {d.device_name}
                                </span>
                                {d.registered_at && (
                                    <span className="text-neutral-400 text-sm">
                                        since {formatNativeAppDate(d.registered_at)}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {report.activity && (
                    <div className="flex items-center gap-3 flex-wrap">
                        <ActivityDot active={report.activity.active_24h} label="24h" />
                        <ActivityDot active={report.activity.active_7d} label="7d" />
                        <ActivityDot active={report.activity.active_30d} label="30d" />
                        {report.activity.last_active && (
                            <span className="text-sm text-neutral-400 ml-auto">
                                Last active:{' '}
                                {formatNativeAppDate(report.activity.last_active, true)}
                            </span>
                        )}
                    </div>
                )}

                {report.notifications && (
                    <div className="space-y-1.5">
                        <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                            Notifications
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600">
                            <span>
                                <span className="font-semibold tabular-nums text-neutral-900">
                                    {report.notifications.sent}
                                </span>{' '}
                                sent
                            </span>
                            <span>
                                <span className="font-semibold tabular-nums text-neutral-900">
                                    {report.notifications.shown}
                                </span>{' '}
                                shown
                            </span>
                            <span>
                                <span className="font-semibold tabular-nums text-neutral-900">
                                    {report.notifications.clicked}
                                </span>{' '}
                                clicked
                            </span>
                            <span>
                                <span className="font-semibold tabular-nums text-neutral-900">
                                    {report.notifications.open_rate}%
                                </span>{' '}
                                open rate
                            </span>
                            <span>
                                <span className="font-semibold text-neutral-900">
                                    {formatNativeAppSeconds(
                                        report.notifications.avg_open_time_seconds,
                                    )}
                                </span>{' '}
                                avg open
                            </span>
                        </div>
                        {report.response_time?.avg_response_time_minutes !== null && (
                            <div className="text-sm text-neutral-600">
                                <span className="font-semibold text-neutral-500 uppercase tracking-wide">
                                    Avg response time (message from lead):{' '}
                                </span>
                                <span className="font-semibold text-neutral-900">
                                    {formatNativeAppMinutes(
                                        report.response_time?.avg_response_time_minutes ?? null,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {report.behavioral && report.behavioral.top_screens.length > 0 && (
                    <div>
                        <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-1">
                            Top screens
                        </p>
                        <div className="max-h-44 overflow-y-auto">
                            <table className="w-full text-sm">
                                <tbody>
                                    {report.behavioral.top_screens.map((s) => (
                                        <tr
                                            key={s.screen_name}
                                            className="border-t border-neutral-100 first:border-t-0">
                                            <td className="py-1 text-neutral-700 truncate">
                                                {s.screen_name}
                                            </td>
                                            <td className="py-1 pl-3 text-right font-semibold tabular-nums text-neutral-900 whitespace-nowrap">
                                                {s.view_count}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    )
}

export default ProviderNativeAppStats
